import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ChatService, CustomersCompaniesService, NotificationService, PillsService, SessionStorageService, UserService } from '@shared/services';
import { BulkMessage, CustomerCompany, Room } from '@shared/factories';
import { MetaModel, UserLocationModel } from '@shared/models';

const TYPES = [
  { type: 'room',     label: 'Mitarbeiter',     icon: 'icon-users'    },
  { type: 'location', label: 'Niederlassungen', icon: 'icon-location' },
  { type: 'company',  label: 'Kunden',          icon: 'icon-case'     }
];

@Component({
  selector:      'bulk-messages-new',
  templateUrl: './bulk-messages-new.component.html',
  host: { class: 'am-flex-column am-flex-1 height-full overflow relative' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BulkMessagesNewComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  @Input() message: BulkMessage;
  types             = TYPES;

  bulkType:         string;
  messageInput:     string;
  loading:          boolean;

  search:           string;
  meta:             MetaModel;

  rooms:            Room[];
  activeRooms:      Room[] = [];

  locations:        UserLocationModel[];
  activeLocation:   UserLocationModel;

  companies:        CustomerCompany[];
  activeCompany:    CustomerCompany;

  @Output() close = new EventEmitter<void>();
  constructor(
    private userService:               UserService,
    private chatService:               ChatService,
    private pillsService:              PillsService,
    private customersCompaniesService: CustomersCompaniesService,
    private sessionStorageService:     SessionStorageService,
    private notificationService:       NotificationService,
  ) { }

  ngOnInit(): void {
    if (this.message) {
      this.messageInput = this.message.text;
      if (this.message.notifiedRooms?.length)     this.bulkType = 'room';
      if (this.message.notifiedLocations?.length) this.bulkType = 'location';
      if (this.message.notifiedCompanies?.length) this.bulkType = 'company';
    } else {
      this.sessionStorageService.searchSecondaryValue.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(search => {
        if      (search?.useCase === 'bulkNew') this.search = search.query;
        else if (!search && this.search)      { this.search = null; this.reloadEntries(); }
      });
      this.pillsService.pills.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resetView());
      this.changeBulkType('room');
    }
  }

  ngOnDestroy(): void {
    if (this.sessionStorageService.searchSecondaryValueValue?.useCase === 'bulkNew') this.sessionStorageService.changeSearchSecondaryValue(null);
  }

  changeBulkType(type: string): void {
    if (type !== this.bulkType) {
      this.sessionStorageService.changeSearchSecondaryValue({ useCase: 'bulkNew', query: null });
      this.search = null;
      this.resetEntries(type);
      this.reloadEntries();
    }
  }

  private resetView(): void {
    this.meta           = null;
    this.rooms          = null;
    this.locations      = null;
    this.companies      = null;

    if (this.bulkType !== 'room'    ) this.activeRooms    = [];
    if (this.bulkType !== 'company' ) this.activeCompany  = null;
    if (this.bulkType !== 'location') this.activeLocation = null;

    this.reloadEntries();
  }

  private resetEntries(type: string): void {
    this[type]    = null;
    this.meta     = null;
    this.bulkType = type;
  }

  reloadEntries(page: number = 1): void {
    if (this.bulkType === 'room'    ) this.reloadRooms(page);
    if (this.bulkType === 'company' ) this.reloadCompanies(page);
    if (this.bulkType === 'location') this.reloadLocations();
  }

  private reloadRooms(page: number = 1): void {
    this.loading = true;
    this.chatService.loadRooms(page, this.search).subscribe(
      ({ rooms, meta }) => {
        this.rooms = rooms;
        this.meta  = meta;
        this.updateActiveRooms();
        this.loading = null;
      },
      err => {
        console.error(err);
        this.loading = null;
      }
    );
  }

  private reloadCompanies(page: number = 1): void {
    this.loading = true;
    this.customersCompaniesService.requestCustomerCompaniesPaginated(page, this.search, 'chat_feature').subscribe(
      ({ companies, meta }) => {
        this.companies = companies;
        this.meta      = meta;
        this.loading   = null;
      },
      err => {
        console.error(err);
        this.loading = null;
      }
    );
  }

  private reloadLocations(): void {
    let locations: UserLocationModel[], beta = this.userService.currentUserValue.features.chat_feature;
    if      ( beta === true)            locations = this.userService.currentUserValue.locations;
    else if ((beta as number[]).length) locations = this.userService.currentUserValue.locations.filter(l => (beta as number[]).find(b => b === l.identifier));
    else                                locations = null;

    if (this.search) locations = locations.filter(l => l.city?.toLowerCase().includes(this.search.toLowerCase()) || (l.identifier+'').includes(this.search));

    let pills = this.sessionStorageService.activePills;
    if (pills?.length) this.locations = locations.filter(l => pills.find(p => p.location === l.identifier))
    else               this.locations = locations;

    if (this.activeLocation && !this.locations.find(l => l.identifier === this.activeLocation.identifier)) this.activeLocation = null;
  }

  select(entry: Room | UserLocationModel | CustomerCompany, event: Event = null): void {
    if (event) event.preventDefault();
    if (this.bulkType === 'room'    ) this.toggleRoom(entry as Room);
    if (this.bulkType === 'company' ) this.activeCompany  = entry as CustomerCompany;
    if (this.bulkType === 'location') this.activeLocation = entry as UserLocationModel;
  }

  private toggleRoom(room: Room): void {
    let active = this.activeRooms.findIndex(r => r.id === room.id);
    if (active > -1) this.activeRooms.splice(active, 1);
    else             this.activeRooms.push(room);
    room.active = !room.active;
  }

  private updateActiveRooms(): void {
    this.activeRooms.forEach(ar => {
      let room = this.rooms.find(r => r.id === ar.id);
      if (room) room.active = true;
    });
  }

  get selectedNames(): string[] {
    if (this.message)                 return this.message.recipientNames;
    if (this.bulkType === 'room'    ) return this.activeRooms.map(r => r.roomName);
    if (this.bulkType === 'company' ) return this.activeCompany  ? [this.activeCompany.name] : [];
    if (this.bulkType === 'location') return this.activeLocation ? [`${this.activeLocation.city} (${this.activeLocation.identifier})`] : [];
    return [];
  }

  get canSubmit(): boolean {
    return !!this.messageInput?.trim() && (!!this.message || this.selectedNames.length > 0);
  }

  get submitLabel(): string {
    if (this.message) return 'Gruppen-Chat bearbeiten';
    let n = this.selectedNames.length;
    if (!n) return 'Senden';
    if (this.bulkType === 'room'    ) return `An ${n} Mitarbeiter senden`;
    if (this.bulkType === 'location') return `An ${n} ${n === 1 ? 'Niederlassung' : 'Niederlassungen'} senden`;
    return `An ${n} Kunden senden`;
  }

  remove(index: number): void {
    if (this.bulkType === 'room'    ) this.removeRoom(this.activeRooms[index]);
    if (this.bulkType === 'company' ) this.activeCompany  = null;
    if (this.bulkType === 'location') this.activeLocation = null;
  }

  clearSelection(): void {
    this.activeRooms.forEach(r => r.active = false);
    this.rooms?.forEach(r => r.active = false);
    this.activeRooms    = [];
    this.activeCompany  = null;
    this.activeLocation = null;
  }

  private removeRoom(room: Room): void {
    if (!room) return;
    this.activeRooms = this.activeRooms.filter(r => r.id !== room.id);
    room.active = false;
    let visible = this.rooms?.find(r => r.id === room.id);
    if (visible) visible.active = false;
  }

  submitHandler(): void {
    if (!this.canSubmit) return;
    let call;
    if (this.message) call = this.chatService.updateBulkMessage(this.messageInput, this.message.id);
    else              call = this.chatService.postBulkMessage(  this.messageInput, this.bulkType, this.getIds());

    call.subscribe(
      res => this.close.emit(),
      err => this.notificationService.alert(err)
    );
  }

  getIds(): number[] {
    if (this.bulkType === 'room'    ) return this.activeRooms.map(ar => ar.id);
    if (this.bulkType === 'company' ) return [this.activeCompany.id];
    if (this.bulkType === 'location') return [this.activeLocation.id];
  }

}
